import React, { useEffect, useMemo, useState } from 'react';
import { goalDataTypeEnums, Images } from 'utils';
import { TimeInputFull } from 'components';
import { Frequency, latencyEnumBtn, Percent, Rating, Scale } from '../session/core/sessionCards/constants';
import {
    emptyTrial,
    formatShortSeconds,
    isAutoAdvanceType,
    isIntervalType,
    isSingleTrialType,
    isTimedType,
    isTrialComplete,
    latencyLimit,
    newTrialId,
    secondsBetween,
    trialErrorFields,
} from './constants';

const isBlank = (t) => t.label === null && t.value === null && !t.startTime && !t.endTime;

const toCount = (raw) => {
    if (raw === '' || raw === null || raw === undefined) return null;
    const n = Math.floor(Number(raw));
    return Number.isFinite(n) && n >= 0 ? n : null;
};

/**
 * Trial navigation and the data-type input for one target on the manual screen. Used by the list
 * row (`ManualTargetRow`) and the pinned strip card (`ManualPinnedCard`), so both edit the same way.
 *
 * PERCENT / RATING / SCORE / FREQUENCY reuse the live session widgets as-is: they only need
 * `item.tSummary`, `current` and a `saveTSummary`, which here writes the page state instead of
 * `PATCH /clinical/data`. The timer types (DURATION / IRT / LATENCY / RATE and the intervals) are
 * live stopwatches, which can't record time after the fact — those take typed start/end times and
 * a Yes/No, as in the Figma.
 */
export const useManualTrialEditor = ({ goal, target, trials, onChange, errorTrial = null }) => {
    const dataType = goal?.dataType;
    const single = isSingleTrialType(dataType);
    const [index, setIndex] = useState(single ? 0 : trials.length);
    const current = trials[index] || emptyTrial();
    // An end time before the start shows at once — not only on Save — and the trial can't be stepped past.
    // Equal times are fine (0s). Every other check stays on Save.
    const endBeforeStart = isTimedType(dataType) && secondsBetween(current.startTime, current.endTime) < 0;
    const timeFields = endBeforeStart ? ['endTime'] : [];
    const currentComplete = isTrialComplete(dataType, current) && !endBeforeStart;
    // The goal's minimum, as the live session shows it ("11/8" past the minimum) — not stretched to the trial on screen.
    const total = goal?.minTrials || 1;

    // Save rejected one of this target's trials: show that trial, with its bad inputs marked.
    useEffect(() => {
        if (errorTrial !== null) setIndex(errorTrial);
    }, [errorTrial]);
    const savedFields = errorTrial !== null && errorTrial === index ? trialErrorFields(goal, current) : [];
    const errorFields = [...new Set([...savedFields, ...timeFields])];

    // The shape the live widgets read: `item.tSummary.trials`, with `current` the trial on screen.
    const item = useMemo(() => ({ ...target, tSummary: { dataType, trials } }), [target, dataType, trials]);
    const widgetCurrent = index < trials.length ? trials[index] : undefined;

    const writeTrial = (patch) => {
        const next = { ...current, ...patch, id: current.id || newTrialId() };
        const list = [...trials];
        if (isBlank(next)) {
            if (index < list.length) list.splice(index, 1);
        } else if (index < list.length) {
            list[index] = next;
        } else {
            list.push(next);
        }
        onChange(list);

        if (!single && isAutoAdvanceType(dataType) && isTrialComplete(dataType, next) && !(secondsBetween(next.startTime, next.endTime) < 0) && index >= trials.length) {
            setIndex(index + 1);
        }
        return Promise.resolve(item.tSummary);
    };

    /** `saveTSummary` for the live widgets. Percent/Rating pass the goal option; Scale/Frequency pass `{ value }`. */
    const saveTSummary = (option) =>
        writeTrial(option?.label !== undefined ? { label: option.label, value: option.value } : { value: option?.value ?? null });

    const clearCurrent = () => {
        if (index >= trials.length) return;
        const list = [...trials];
        list.splice(index, 1);
        onChange(list);
        setIndex(single ? 0 : Math.min(index, list.length));
    };

    const labeled = (label, field) => (
        <div className="manual-field">
            <p className="manual-field-label">{label}</p>
            {field}
        </div>
    );

    const timeInput = (field, label) =>
        labeled(label,
            <TimeInputFull
                key={`${target?.id}-${index}-${field}`}
                name={field}
                customClass="manual-time-input"
                errorFalse={true}
                typeError={errorFields.includes(field)}
                defaultValue={current[field]}
                onChange={(e) => writeTrial({ [field]: e?.target?.value || null })}
            />
        );

    /** `limit` (LATENCY only) is the goal's "Expired in" window: shown in the label, and the box turns red past it. */
    const durationBox = (limit = null) => {
        const secs = secondsBetween(current.startTime, current.endTime);
        const over = !!limit && secs !== null && secs > limit.seconds;
        return labeled(
            limit ? `Latency (max ${limit.label})` : 'Duration',
            <div className={`manual-readonly-box ${over ? 'manual-input-error' : ''}`}>
                {secs === null || secs < 0 ? '-' : formatShortSeconds(secs)}
            </div>
        );
    };

    /** The timed inputs plus, when the window is wrong, why — a red input alone doesn't say. */
    const timedInputs = (children) => (
        <div className="manual-timed-wrap">
            <div className="manual-timed-inputs">{children}</div>
            {endBeforeStart && <p className="manual-field-error">End time must be the same as or after the start time.</p>}
        </div>
    );

    const renderInput = () => {
        switch (dataType) {
            case goalDataTypeEnums.PERCENT:
                return <Percent item={item} goal={goal} current={widgetCurrent} saveTSummary={saveTSummary} grid={true} index={index} />;

            case goalDataTypeEnums.RATING:
                return <Rating item={item} goal={goal} current={widgetCurrent} saveTSummary={saveTSummary} grid={true} index={index} />;

            case goalDataTypeEnums.SCORE:
                return <Scale item={item} current={widgetCurrent} saveTSummary={saveTSummary} grid={true} index={index} />;

            case goalDataTypeEnums.FREQUENCY:
                return <Frequency item={item} saveTSummary={saveTSummary} />;

            case goalDataTypeEnums.RATE:
                return timedInputs(
                    <>
                        {timeInput('startTime', 'Start Time')}
                        {timeInput('endTime', 'End Time')}
                        {labeled(
                            'Count',
                            <input
                                className={`manual-number-input ${errorFields.includes('value') ? 'manual-input-error' : ''}`}
                                type="number"
                                min={0}
                                placeholder="0"
                                value={current.value ?? ''}
                                onChange={(e) => writeTrial({ value: toCount(e.target.value) })}
                            />
                        )}
                    </>
                );

            case goalDataTypeEnums.DURATION:
            case goalDataTypeEnums.IRT:
                return timedInputs(
                    <>
                        {timeInput('startTime', 'Start Time')}
                        {timeInput('endTime', 'End Time')}
                        {durationBox()}
                    </>
                );

            case goalDataTypeEnums.LATENCY:
                return timedInputs(
                    <>
                        {timeInput('startTime', 'Start Time')}
                        {timeInput('endTime', 'End Time')}
                        {durationBox(latencyLimit(goal))}
                        <div className="flex-align-center" style={{ gap: 16, alignSelf: 'flex-end' }}>
                            {latencyEnumBtn.map((b) => (
                                <button
                                    type="button"
                                    key={b.value}
                                    className={`${b.class} ${current.label === b.value ? 'active' : ''}`}
                                    style={{ width: 145 }}
                                    onClick={() => writeTrial({ label: b.value })}
                                >
                                    <img src={b.icon} alt="" style={{ width: 20, height: 20 }} />
                                    {b.title}
                                </button>
                            ))}
                        </div>
                    </>
                );

            default:
                if (!isIntervalType(dataType)) return null;
                return (
                    <div className="flex-align-center" style={{ gap: 16 }}>
                        <button
                            type="button"
                            style={{ width: 76 }}
                            onClick={() => writeTrial({ value: 1 })}
                            className={`target-status-blue-btn-box ${current.value === 1 ? 'active' : ''}`}
                        >
                            <img src={Images.checkmarkWhite} alt="" />
                            Yes
                        </button>
                        <button
                            type="button"
                            style={{ width: 76 }}
                            onClick={() => writeTrial({ value: 0 })}
                            className={`target-status-no-response-btn-box ${current.value === 0 ? 'active' : ''}`}
                        >
                            <img src={Images.remove} alt="" style={{ width: 20, height: 20 }} />
                            No
                        </button>
                    </div>
                );
        }
    };

    return { single, index, setIndex, total, current, currentComplete, item, widgetCurrent, renderInput, clearCurrent, errorFields };
};
